import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Flame, ShoppingBag } from "lucide-react";
import { NAV, SITE } from "../lib/data";
import { useCart } from "../lib/cartContext";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const { count, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On subpages we always treat as scrolled so navbar is on white background
  const navOnDark = isHome && !scrolled;

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          !navOnDark
            ? "bg-white/90 backdrop-blur-xl border-b border-pkash"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-2 group"
          >
            <Flame
              className={`w-5 h-5 ${
                !navOnDark ? "text-pkred" : "text-white"
              } group-hover:text-pkred transition-colors`}
              strokeWidth={1.5}
            />
            <span
              className={`font-serif text-xl tracking-[0.25em] ${
                !navOnDark ? "text-pkcharcoal" : "text-white"
              }`}
            >
              {SITE.brand}
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((item) => {
              const active = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`flame-nav text-[0.72rem] font-medium tracking-[0.18em] uppercase transition-colors duration-300 ${
                    active ? "text-pkred" : !navOnDark ? "text-pkcharcoal" : "text-white/90 flame-nav-light"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <a
              href={SITE.phoneHref}
              data-testid="nav-phone-cta"
              className="btn-fire !py-2.5 !px-5 !text-xs"
            >
              Asztalfoglalás
            </a>
            <button
              type="button"
              data-testid="nav-cart-button"
              onClick={() => setCartOpen(true)}
              aria-label={`Kosár (${count} termék)`}
              className={`relative w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${
                !navOnDark
                  ? "border-pkash text-pkcharcoal hover:border-pkred hover:text-pkred"
                  : "border-white/40 text-white hover:border-pkred hover:text-pkred"
              }`}
            >
              <ShoppingBag className="w-4 h-4" strokeWidth={1.6} />
              {count > 0 && (
                <span
                  data-testid="nav-cart-count"
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-pkred text-white text-[0.6rem] font-bold flex items-center justify-center shadow-[0_0_10px_rgba(217,4,41,0.7)]"
                >
                  {count}
                </span>
              )}
            </button>
          </nav>

          <div className="lg:hidden flex items-center gap-3">
            <button
              type="button"
              data-testid="mobile-cart-button"
              onClick={() => setCartOpen(true)}
              aria-label={`Kosár (${count} termék)`}
              className="relative"
            >
              <ShoppingBag
                className={`w-6 h-6 ${
                  !navOnDark ? "text-pkcharcoal" : "text-white"
                }`}
                strokeWidth={1.5}
              />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-pkred text-white text-[0.55rem] font-bold flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              aria-label="Menü"
            >
              <Menu
                className={`w-7 h-7 ${
                  !navOnDark ? "text-pkcharcoal" : "text-white"
                }`}
                strokeWidth={1.5}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] transition-opacity duration-500 lg:hidden ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="mobile-menu-drawer"
      >
        <div
          className="absolute inset-0 bg-pkcharcoal/80 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
        <div
          className={`absolute top-0 right-0 h-full w-[85%] max-w-sm bg-white shadow-2xl transition-transform duration-500 ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between p-6 border-b border-pkash">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-pkred" strokeWidth={1.5} />
              <span className="font-serif text-lg tracking-[0.25em] text-pkcharcoal">
                {SITE.brand}
              </span>
            </div>
            <button
              data-testid="mobile-menu-close"
              onClick={() => setOpen(false)}
              aria-label="Bezárás"
            >
              <X className="w-6 h-6 text-pkcharcoal" strokeWidth={1.5} />
            </button>
          </div>
          <nav className="flex flex-col p-8 gap-1 overflow-y-auto max-h-[calc(100%-100px)]">
            {NAV.map((item, idx) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className="flame-nav font-serif text-2xl text-pkcharcoal py-3 border-b border-pkash transition-colors"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                {item.label}
              </Link>
            ))}
            <a
              href={SITE.phoneHref}
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-call"
              className="btn-fire mt-8"
            >
              Asztalfoglalás
            </a>
            <a
              href={SITE.emailHref}
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-email"
              className="btn-ghost-dark mt-3"
            >
              Email küldése
            </a>
          </nav>
        </div>
      </div>
    </>
  );
}
